import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import { auth, db } from '@/firebase/init';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { saveJourneyProgress, completeJourney as completeJourneyFirebase, deleteJourney } from '@/firebase/journeyService';
import { syncJourney as syncJourneyPersistence, deleteJourney as deleteJourneyPersistence } from '@/services/journeyPersistence';

const getProductsFromSource = (source) => {
    if (!source || typeof source !== 'object') return null;
    if (Array.isArray(source.setProducts)) return source.setProducts;
    if (Array.isArray(source.fullControlSets)) return source.fullControlSets;
    return null;
};

const personalizationEntryHasPortraitStyle = (entry) => {
    if (!entry || entry.input == null) return false;
    const input = entry.input;
    if (Array.isArray(input)) {
        return input.some((inputItem) => !!(inputItem && inputItem.portraitStyle));
    }
    if (typeof input === 'object') {
        if (input.portraitStyle) return true;
    }
    return false;
};

const productHasPortraitStyle = (product) => {
    if (!product || typeof product !== 'object') return false;
    if (product.portraitStyle) return true;
    if (product.userSelections?.portraitStyle) return true;
    if (Array.isArray(product.personalization)) {
        return product.personalization.some((entry) => personalizationEntryHasPortraitStyle(entry));
    }
    return false;
};

const computePortraitStyleCount = (source) => {
    const products = getProductsFromSource(source);
    if (!products) return null;
    return products.reduce(
        (count, product) => count + (productHasPortraitStyle(product) ? 1 : 0),
        0
    );
};

const store = createStore({
    state: {
        user: null,
        cart: [],
        products: [],
        journeys: {}, // { [productId]: { productId, productName, step, isComplete, updatedAt } }
        shipping: {
            address: {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                country: '',
                state: '',
                city: '',
                street: '',
                zip: '',
            },
            method: 'standard',
        },
    },
    mutations: {
        loginUser(state, payload) {
            state.user = payload;
        },
        logoutUser(state) {
            state.user = null;
        },
        addToCart(state, payload) {
            if (state.cart.length != 0) {
                //check if the item is in the cart
                let item = state.cart.find((item) => item._id === payload._id);

                //if the item is in the cart and has the same id and size, increase the quantity
                if (item) {
                    if (item.size === payload.size) {
                        //if the item's quantity is less than 10 add more
                        if (item.quantity < 10) {
                            item.quantity += payload.quantity;
                        } else if (item.quantity >= 10) {
                            //if the item is already 10, don't add more
                            item.quantity = 10;
                        }
                    } else {
                        //if the item exists but has a different size, add the new item
                        console.log("different size");
                        state.cart = [...state.cart, payload];
                    }
                } else {
                    //if the item is not in the cart, add it
                    state.cart = [...state.cart, payload];
                }
            } else {
                //if the cart is empty, add the item
                state.cart = [...state.cart, payload];
            }
        },
        deleteItem(state, payload) {
            state.cart.splice(payload, 1);
        },
        updateQuantity(state, payload) {
            // increase the quantity of the item in the cart
            if (payload.quantity > 0) {
                let item = state.cart.find(
                    (item) =>
                        item._id === payload._id && item.size === payload.size
                );
                item.quantity = payload.quantity;
            }
        },
        emptyCart(state) {
            state.cart = [];
        },
        setCart(state, payload) {
            state.cart = Array.isArray(payload) ? payload : [];
        },
        setProducts(state, payload) {
            state.products = payload;
        },
        setJourneyProgress(state, payload) {
            const {
                productId,
                productName,
                step,
                isComplete,
                profileType,
                petName,
                petType,
                instagram,
                images,
                comments,
                theme,
                product,
                productPrice,
                fullControlSets,
                fullControlState,
                journeyType,
                setProducts, // New structure
                setName, // New structure
            } = payload;
            // Ignore invalid saves (can happen on race at page load)
            if (!productId) return;
            const prev = state.journeys?.[productId] || {};
            
            const portraitStyleCountFromPayload = computePortraitStyleCount(payload);
            const updatedJourney = {
                ...prev,
                productId,
                productName: productName ?? prev.productName,
                // Preserve previous values when fields are omitted
                step: typeof step === 'number' ? step : (prev.step ?? 0),
                isComplete: typeof isComplete === 'boolean' ? isComplete : (prev.isComplete ?? false),
                profileType: profileType ?? prev.profileType,
                petName: petName ?? prev.petName,
                petType: petType ?? prev.petType,
                instagram: instagram ?? prev.instagram,
                images: images ?? prev.images,
                comments: comments ?? prev.comments,
                theme: theme ?? prev.theme,
                product: product ?? prev.product,
                productPrice: productPrice ?? prev.productPrice,
                fullControlSets: fullControlSets ?? prev.fullControlSets,
                fullControlState: fullControlState ?? prev.fullControlState,
                journeyType: journeyType ?? prev.journeyType,
                portraitStyleCount: portraitStyleCountFromPayload !== null
                    ? portraitStyleCountFromPayload
                    : (typeof prev.portraitStyleCount === 'number' ? prev.portraitStyleCount : 0),
                // New structure fields
                setProducts: setProducts ?? prev.setProducts,
                setName: setName ?? prev.setName,
                updatedAt: Date.now(),
            };
            
            state.journeys = {
                ...state.journeys,
                [productId]: updatedJourney
            };
            
            // STEP 3: Log what's being stored in Vuex
            console.log('🔄 STEP 3 - Vuex setJourneyProgress mutation');
            if (fullControlSets) {
                const productsWithData = fullControlSets.filter(s => s.personalization?.length > 0);
                console.log(`   💾 VUEX STORE: ${productsWithData.length}/${fullControlSets.length} products with personalization`);
                
                // Log details of products with data
                productsWithData.forEach(set => {
                    console.log(`   ✅ ${set.name}: ${set.personalization.length} entries`);
                });
            } else {
                console.log('   ⚠️ fullControlSets is undefined/null');
            }
        },
        completeJourney(state, productId) {
            if (!state.journeys[productId]) return;
            state.journeys[productId] = {
                ...state.journeys[productId],
                isComplete: true,
                updatedAt: Date.now(),
            };
        },
        deleteJourney(state, productId) {
            if (!state.journeys) return;
            const copy = { ...(state.journeys || {}) };
            delete copy[productId];
            state.journeys = copy;
        },
        setShippingAddress(state, payload) {
            state.shipping = {
                ...state.shipping,
                address: { ...state.shipping.address, ...(payload || {}) },
            };
        },
        setShippingMethod(state, method) {
            state.shipping = { ...state.shipping, method };
        },
    },
    actions: {
        set_user(context, payload) {
            context.commit("loginUser", payload);
        },
        remove_user(context) {
            context.commit("logoutUser");
        },
        add_to_cart(context, payload) {
            console.log('🔵 [STORE] add_to_cart action called');
            console.log('🔵 [STORE] Cart item payload:', JSON.stringify(payload, (key, value) => {
                if (value instanceof File) return '[File object]';
                return value;
            }, 2));
            console.log('🔵 [STORE] Current cart size:', context.state.cart.length);
            
            context.commit("addToCart", payload);
            
            console.log('✅ [STORE] Item added to cart');
            console.log('🔵 [STORE] New cart size:', context.state.cart.length);
            console.log('🔵 [STORE] Persisting cart...');
            
            context.dispatch('persist_cart');
            
            console.log('✅ [STORE] Cart persisted');
        },
        async delete_item(context, payload) {
            // Get the item before deleting to extract productId
            const item = context.state.cart[payload];
            
            // Delete from cart
            context.commit("deleteItem", payload);
            context.dispatch('persist_cart');
            
            // Delete journey data and images from Firebase
            if (item && item._id) {
                try {
                    // Delete from Vuex journeys
                    context.commit("deleteJourney", item._id);
                    
                    // Delete from Firebase (includes Firestore document and Storage images)
                    await deleteJourney(item._id);
                    console.log('Journey and images deleted for:', item._id);
                } catch (error) {
                    console.error('Error deleting journey data:', error);
                    // Don't throw - cart item is already removed
                }
            }
        },
        update_quantity(context, payload) {
            context.commit("updateQuantity", payload);
            context.dispatch('persist_cart');
        },
        clear_cart(context) {
            context.commit("emptyCart");
            context.dispatch('persist_cart');
        },
        async clear_cart_and_journeys(context) {
            try {
                const count = context.state.cart.length;
                // Iterate from the end so indexes stay valid as we delete
                for (let i = count - 1; i >= 0; i -= 1) {
                    await context.dispatch('delete_item', i);
                }
            } catch (error) {
                console.error('❌ [STORE] clear_cart_and_journeys failed:', error);
            }
        },
        set_products(context, payload) {
            context.commit("setProducts", payload);
        },
        async set_journey_progress(context, payload) {
            console.log('🔵 [STORE] set_journey_progress action called');
            console.log('🔵 [STORE] Journey payload:', {
                productId: payload.productId,
                productName: payload.productName,
                journeyType: payload.journeyType,
                step: payload.step,
                isComplete: payload.isComplete,
                hasImages: !!payload.images,
                imageCount: payload.images?.length || 0,
                hasImageUrls: !!payload.imageUrls,
                imageUrlCount: payload.imageUrls?.length || 0,
            });
            
            context.commit("setJourneyProgress", payload);
            
            console.log('✅ [STORE] Journey committed to Vuex');
            
            // Auto-sync to Firebase (old method for backward compatibility)
            try {
                if (payload.productId) {
                    console.log('🔵 [STORE] Syncing journey to Firebase...');
                    await saveJourneyProgress(payload.productId, payload);
                    console.log('✅ [STORE] Journey synced to Firebase');
                }
            } catch (error) {
                console.error('❌ [STORE] Failed to sync journey to Firebase:', error);
                console.error('❌ [STORE] Error details:', error.message);
            }
        },
        async sync_journey_new_structure(context, journey) {
            // Sync using new persistence service (cookies + Firebase)
            try {
                await syncJourneyPersistence(journey);
                console.log('Journey synced with new structure:', journey.journeyId);
            } catch (error) {
                console.error('Failed to sync journey with new structure:', error);
            }
        },
        async complete_journey(context, productId) {
            console.log('🔵 [STORE] complete_journey action called');
            console.log('🔵 [STORE] Journey ID:', productId);
            
            context.commit("completeJourney", productId);
            
            console.log('✅ [STORE] Journey marked complete in Vuex');
            
            // Sync completion to Firebase
            try {
                console.log('🔵 [STORE] Syncing journey completion to Firebase...');
                await completeJourneyFirebase(productId);
                console.log('✅ [STORE] Journey completion synced to Firebase');
            } catch (error) {
                console.error('❌ [STORE] Failed to sync journey completion to Firebase:', error);
                console.error('❌ [STORE] Error details:', error.message);
            }
        },
        async delete_journey(context, productId) {
            context.commit("deleteJourney", productId);
            // Also delete from new persistence system
            try {
                await deleteJourneyPersistence(productId);
            } catch (error) {
                console.warn('Failed to delete journey from new persistence:', error);
            }
        },
        cleanup_empty_journeys(context) {
            // Remove journeys that have no actual progress
            const journeys = context.state.journeys || {};
            Object.keys(journeys).forEach(productId => {
                const journey = journeys[productId];
                const hasProgress = 
                    (journey.step && journey.step > 0) ||
                    (journey.images && journey.images.length > 0) ||
                    journey.petName ||
                    journey.profileType ||
                    journey.theme;
                
                if (!hasProgress) {
                    console.log('Cleaning up empty journey:', productId);
                    context.commit("deleteJourney", productId);
                }
            });
        },
        set_shipping_address(context, payload) {
            context.commit('setShippingAddress', payload);
        },
        set_shipping_method(context, method) {
            context.commit('setShippingMethod', method);
        },
        async persist_cart({ state }) {
            try {
                const user = auth.currentUser;
                if (!user) return; // only persist for logged-in users
                const ref = doc(db, 'carts', user.uid);
                await setDoc(ref, {
                    items: state.cart || [],
                    updatedAt: Date.now(),
                }, { merge: true });
            } catch (e) {
                // swallow errors to not break UX
                // console.warn('Failed to persist cart', e);
            }
        },
        async load_cart_for_user({ commit }) {
            try {
                const user = auth.currentUser;
                if (!user) return;
                const ref = doc(db, 'carts', user.uid);
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const data = snap.data();
                    commit('setCart', data?.items || []);
                }
            } catch (e) {
                // console.warn('Failed to load cart', e);
            }
        },
        async checkFirstTimeBuyer(context) {
            try {
                const user = auth.currentUser;
                if (!user) return false;
                
                // Check if user has any orders
                const ordersRef = collection(db, "orders");
                const q = query(ordersRef, where("accountUid", "==", user.uid));
                const querySnapshot = await getDocs(q);
                
                return querySnapshot.empty;
            } catch (error) {
                console.error("Error checking first time buyer:", error);
                return false;
            }
        },
    },
    getters: {
        subtotal: (state) => {
            let subtotal = 0;
            state.cart.forEach((item) => {
                subtotal += item.price * item.quantity;
            });
            return subtotal;
        },
        shippingAddress: (state) => state.shipping.address,
        shippingMethod: (state) => state.shipping.method,
        getHash: (state) => {
            var md5 = require("blueimp-md5/js/md5");
            var hash = md5(state.user?.email);
            return `https://www.gravatar.com/avatar/` + hash + `?d=mp`;
        },
        hasIncompleteJourneys: (state) => {
            return Object.values(state.journeys).some((j) => !j.isComplete);
        },
        getJourneyByProductId: (state) => (productId) => {
            return state.journeys[productId];
        },
        getJourneyByProductName: (state) => (productName) => {
            const entries = Object.values(state.journeys || {});
            return entries.find((j) => j.productName === productName);
        },
    },
    plugins: [
        createPersistedState({
            // Reduce what gets persisted to localStorage to avoid quota errors
            reducer: (state) => ({
                user: state.user,
                cart: state.cart,
                shipping: state.shipping,
                // For journeys, only persist minimal metadata (no images, no large data)
                journeys: Object.keys(state.journeys || {}).reduce((acc, key) => {
                    const journey = state.journeys[key];
                    acc[key] = {
                        productId: journey.productId,
                        productName: journey.productName,
                        step: journey.step,
                        isComplete: journey.isComplete,
                        journeyType: journey.journeyType,
                        updatedAt: journey.updatedAt,
                    portraitStyleCount: journey.portraitStyleCount,
                        // Don't persist images, fullControlSets with thumbnails, or other large data
                        // These are stored in Firebase and loaded on demand
                    };
                    return acc;
                }, {})
            })
        })
    ],
});

// Sync cart with Firestore on auth state changes
onAuthStateChanged(auth, async (user) => {
    if (user) {
        await store.dispatch('load_cart_for_user');
    } else {
        store.commit('setCart', []);
    }
});

//export store
export default store;

// Helper to print store state (call from console: window.printVuexJourneys())
if (typeof window !== 'undefined') {
    window.printVuexJourneys = function() {
        const journeys = store.state.journeys;
        console.log('🗂️ VUEX JOURNEYS STATE:');
        Object.keys(journeys).forEach(journeyId => {
            const journey = journeys[journeyId];
            console.log(`\n📁 Journey: ${journeyId}`);
            console.log(`   Product: ${journey.productName}`);
            console.log(`   Complete: ${journey.isComplete}`);
            if (journey.fullControlSets) {
                journey.fullControlSets.forEach((set, idx) => {
                    const personalizationCount = set.personalization?.length || 0;
                    console.log(`   ${idx}. ${set.name}: ${personalizationCount} personalization entries`);
                    if (personalizationCount > 0) {
                        set.personalization.forEach(entry => {
                            if (entry.type === 'pictures') {
                                console.log(`      - ${entry.type}: ${entry.input?.length || 0} images`);
                            } else {
                                console.log(`      - ${entry.type}: ${entry.input}`);
                            }
                        });
                    }
                });
            }
        });
    };
}
